/** biome-ignore-all lint/complexity/noExcessiveLinesPerFunction: <explanation> */
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import { useEffect } from "react";

import { InitPassword, SetupPassword } from "~/API/Account";
import { Upload } from "~/API/Attachment";
import { GetCities } from "~/API/cities";
import {
  AddTicket,
  AddUpdateSupplierOffer,
  CreateUserSupplier,
  DeleteAwards,
  DeleteSupplierOffer,
  DeleteUserSupplier,
  GetAllQuestions,
  GetAllSupplierOffers,
  GetAllTickets,
  GetCategories,
  GetSupplierProfile,
  GetSupplierTypes,
  Overview,
  SearchCategories,
  SubmitSupplier,
  UpdateAwards,
  UpdateOffer,
  UpdateQuestionnaire,
  UpdateSupplier,
  UpdateUserSupplier,
} from "~/API/Supplier";
import type {
  CategoryDetailsView,
  CategorySearchView,
  CreateSupplierUser,
  CreateSupplierUsers,
  CreateTicketRequest,
  CreateUpdateAwardRequest,
  CreateUpdateOfferingRequest,
  GenericDataResponseQuestionView,
  ISuppliersFilters,
  OfferingDetailsView,
  PaginatedResponseCityView,
  PaginatedResponseTicketView,
  SetupPasswordRequest,
  SupplierAttachmentViewType,
  SupplierDetailsView,
  SupplierOverviewResponse,
  SupplierQuestionnaireRequest,
  SupplierType,
  UpdateSupplierRequest,
} from "~/Core/Models";
import type { QueryMutationResult, QueryResult } from "~/Store/globals";

import { useAccountStore } from "../Account";

const SupplierInfo = (_set, _get) => ({
  AddTicket(): QueryMutationResult<CreateTicketRequest> {
    return useMutation({
      mutationFn: AddTicket,
    });
  },

  AddUpdateSupplierOffer(): QueryMutationResult<CreateUpdateOfferingRequest> {
    return useMutation({
      mutationFn: AddUpdateSupplierOffer,
    });
  },
  // /////////////////////////////////////////////////////////////////////////////////   Users  ///
  CreateSupplierUser(): QueryMutationResult<CreateSupplierUsers> {
    return useMutation({
      mutationFn: CreateUserSupplier,
    });
  },

  DeleteAwards(): QueryMutationResult<string | number> {
    return useMutation({
      mutationFn: DeleteAwards,
    });
  },
  DeleteSupplierOffer(): QueryMutationResult<string | number> {
    return useMutation({
      mutationFn: DeleteSupplierOffer,
    });
  },

  DeleteUserSupplier(): QueryMutationResult<string | number> {
    return useMutation({
      mutationFn: DeleteUserSupplier,
    });
  },
  GetAllQuestions(): QueryResult<AxiosResponse<GenericDataResponseQuestionView>> {
    return useQuery({
      queryFn: GetAllQuestions,
      queryKey: ["GetAllQuestions"],
    });
  },

  // ////////////////////////////////////////////////////////////////////   Offer    ///
  GetAllSupplierOffers(): QueryResult<AxiosResponse<OfferingDetailsView[]>> {
    return useQuery({
      queryFn: GetAllSupplierOffers,
      queryKey: ["OfferingDetailsView"],
      staleTime: 0,
    });
  },

  // //////////////////////////////////////////////////////////////////////////
  GetAllTickets(filters: ISuppliersFilters): QueryResult<AxiosResponse<PaginatedResponseTicketView>> {
    return useQuery({
      placeholderData: keepPreviousData,
      queryFn: () => GetAllTickets(filters),
      queryKey: ["GetAllSupplier", { ...filters }],
    });
  },

  GetCategories(enabled: boolean, parents?: string[]): QueryResult<AxiosResponse<CategoryDetailsView[]>> {
    return useQuery({
      enabled,
      placeholderData: keepPreviousData,
      queryFn: () => GetCategories(parents),
      queryKey: ["GetCategories", enabled, parents],
    });
  },

  GetCities(): QueryResult<AxiosResponse<PaginatedResponseCityView>> {
    return useQuery({
      queryFn: GetCities,
      queryKey: ["GetCities"],
    });
  },

  GetSupplierProfile(): QueryResult<AxiosResponse<SupplierDetailsView>> {
    const q = useQuery({
      gcTime: 0,
      queryFn: () => {
        const res = GetSupplierProfile();

        return res;
      },
      queryKey: ["SupplierDetails"],
      staleTime: 0,
    });
    useEffect(() => {
      if (q.status === "success" && q.data?.data) {
        const supplier = q.data.data;
        useAccountStore.setState(state => {
          if (!state.UserProfile) return state;
          return {
            ...state,
            UserProfile: {
              ...state.UserProfile,
              profile: {
                ...state.UserProfile.profile,
                supplier,
              },
            },
          };
        });
      }
    }, [q.data]);
    return q;
  },

  GetSupplierTypes(): QueryResult<AxiosResponse<SupplierType[]>> {
    return useQuery({
      queryFn: GetSupplierTypes,
      queryKey: ["GetSupplierTypes"],
    });
  },

  InitPassword(): QueryMutationResult<SetupPasswordRequest> {
    return useMutation({
      mutationFn: InitPassword,
    });
  },

  Overview(): QueryResult<AxiosResponse<SupplierOverviewResponse>> {
    return useQuery({
      queryFn: Overview,
      queryKey: ["Overview"],
    });
  },

  SearchCategories(enabled: boolean, query?: string): QueryResult<AxiosResponse<CategorySearchView[]>> {
    return useQuery({
      enabled,
      queryFn: () => SearchCategories(query),
      queryKey: ["SearchCategories", enabled, query],
    });
  },
  SetupPassword(): QueryMutationResult<SetupPasswordRequest> {
    return useMutation({
      mutationFn: SetupPassword,
    });
  },

  SubmitSupplier(): QueryMutationResult<void> {
    return useMutation({
      mutationFn: SubmitSupplier,
    });
  },

  // //////////////////////////////////////////////////////////////////////////////////   Awards  ///
  UpdateAwards(): QueryMutationResult<CreateUpdateAwardRequest> {
    return useMutation({
      mutationFn: UpdateAwards,
    });
  },
  UpdateOffer(): QueryMutationResult<CreateUpdateOfferingRequest> {
    return useMutation({
      mutationFn: UpdateOffer,
    });
  },

  // /////////////////////////////////// Questionnaire

  UpdateQuestionnaire(): QueryMutationResult<SupplierQuestionnaireRequest> {
    return useMutation({
      mutationFn: UpdateQuestionnaire,
    });
  },

  UpdateSupplier(): QueryMutationResult<UpdateSupplierRequest> {
    return useMutation({
      mutationFn: UpdateSupplier,
    });
  },

  UpdateUserSupplier(): QueryMutationResult<CreateSupplierUser> {
    return useMutation({
      mutationFn: UpdateUserSupplier,
    });
  },

  Upload(): QueryMutationResult<{ type: SupplierAttachmentViewType; file: File }> {
    return useMutation({
      mutationFn: Upload,
    });
  },
});

export default SupplierInfo;
